import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Table, Badge, Alert } from 'react-bootstrap';
import { FaCheck, FaTimes, FaBan } from 'react-icons/fa';

const BulkEditAsignacionesModal = ({
    show,
    onHide,
    selectedAsignaciones,
    asignaciones,
    onConfirm,
    loading,
    formatearMonto
}) => {
    const [selectedAction, setSelectedAction] = useState(null);

    // Filtrar asignaciones seleccionadas
    const selectedAsignacionesData = asignaciones.filter(asignacion =>
        selectedAsignaciones.includes(asignacion.id)
    );

    // Filtrar asignaciones válidas para cada acción
    const getValidAsignacionesForAction = (action) => {
        switch (action) {
            case 'aprobar':
                return selectedAsignacionesData.filter(asignacion =>
                    asignacion.estado?.estado === 'creado'
                );
            case 'rechazar':
                return selectedAsignacionesData.filter(asignacion =>
                    asignacion.estado?.estado === 'creado'
                );
            case 'cancelar':
                return selectedAsignacionesData.filter(asignacion =>
                    asignacion.estado?.estado === 'aprobado'
                );
            default:
                return [];
        }
    };

    const validAsignaciones = selectedAction ? getValidAsignacionesForAction(selectedAction) : [];

    const handleConfirm = () => {
        if (!selectedAction || validAsignaciones.length === 0) return;

        const asignacionIds = validAsignaciones.map(asignacion => asignacion.id);
        onConfirm(selectedAction, asignacionIds);
    };

    const handleClose = () => {
        setSelectedAction(null);
        onHide();
    };

    const getActionLabel = (action) => {
        switch (action) {
            case 'aprobar': return 'Aprobar';
            case 'rechazar': return 'Rechazar';
            case 'cancelar': return 'Cancelar';
            default: return '';
        }
    };

    const getActionVariant = (action) => {
        switch (action) {
            case 'aprobar': return 'success';
            case 'rechazar': return 'danger';
            case 'cancelar': return 'warning';
            default: return 'secondary';
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaCheck className="me-2" />
                    Edición Masiva de Asignaciones
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="mb-4">
                    <h6>Asignaciones seleccionadas: {selectedAsignacionesData.length}</h6>
                    <div className="table-responsive">
                        <Table striped bordered hover size="sm">
                            <thead className="table-primary">
                                <tr>
                                    <th>Conductor</th>
                                    <th>Extra</th>
                                    <th>Monto Total</th>
                                    <th>Estado Actual</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedAsignacionesData.map(asignacion => (
                                    <tr key={asignacion.id}>
                                        <td>
                                            <strong>{asignacion.usuario_movil?.usuarioSpread || 'N/A'}</strong>
                                        </td>
                                        <td>{asignacion.extra?.extra || 'N/A'}</td>
                                        <td>
                                            <strong>
                                                {formatearMonto(
                                                    asignacion.monto * (asignacion.cantidad || 1),
                                                    asignacion.extra?.tipo_extra?.tipo
                                                )}
                                            </strong>
                                        </td>
                                        <td>
                                            <Badge
                                                bg={
                                                    asignacion.estado?.estado === 'creado' ? 'warning' :
                                                    asignacion.estado?.estado === 'aprobado' ? 'success' :
                                                    asignacion.estado?.estado === 'rechazado' ? 'danger' :
                                                    'secondary'
                                                }
                                            >
                                                {asignacion.estado?.estado || 'Sin Estado'}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>

                <div className="mb-4">
                    <h6>Seleccionar acción:</h6>
                    <div className="d-flex gap-2 flex-wrap">
                        <Button
                            variant={selectedAction === 'aprobar' ? 'success' : 'outline-success'}
                            onClick={() => setSelectedAction('aprobar')}
                            className="d-flex align-items-center"
                        >
                            <FaCheck className="me-2" />
                            Aprobar ({getValidAsignacionesForAction('aprobar').length})
                        </Button>
                        <Button
                            variant={selectedAction === 'rechazar' ? 'danger' : 'outline-danger'}
                            onClick={() => setSelectedAction('rechazar')}
                            className="d-flex align-items-center"
                        >
                            <FaTimes className="me-2" />
                            Rechazar ({getValidAsignacionesForAction('rechazar').length})
                        </Button>
                        <Button
                            variant={selectedAction === 'cancelar' ? 'warning' : 'outline-warning'}
                            onClick={() => setSelectedAction('cancelar')}
                            className="d-flex align-items-center"
                        >
                            <FaBan className="me-2" />
                            Cancelar ({getValidAsignacionesForAction('cancelar').length})
                        </Button>
                    </div>
                </div>

                {selectedAction && (
                    <Alert variant="info">
                        <strong>Acción seleccionada:</strong> {getActionLabel(selectedAction)}
                        <br />
                        <strong>Asignaciones que serán afectadas:</strong> {validAsignaciones.length}
                        {validAsignaciones.length !== selectedAsignacionesData.length && (
                            <div className="mt-2">
                                <small className="text-muted">
                                    * Solo las asignaciones con estado válido para esta acción serán procesadas.
                                    Las demás serán ignoradas.
                                </small>
                            </div>
                        )}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancelar
                </Button>
                <Button
                    variant={getActionVariant(selectedAction)}
                    onClick={handleConfirm}
                    disabled={!selectedAction || validAsignaciones.length === 0 || loading}
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Procesando...
                        </>
                    ) : (
                        <>
                            {selectedAction === 'aprobar' && <FaCheck className="me-2" />}
                            {selectedAction === 'rechazar' && <FaTimes className="me-2" />}
                            {selectedAction === 'cancelar' && <FaBan className="me-2" />}
                            {getActionLabel(selectedAction)} ({validAsignaciones.length})
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

BulkEditAsignacionesModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    selectedAsignaciones: PropTypes.array.isRequired,
    asignaciones: PropTypes.array.isRequired,
    onConfirm: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    formatearMonto: PropTypes.func.isRequired
};

export default BulkEditAsignacionesModal;